import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';

export const WidgetReadyContext = createContext<(() => void) | undefined>(
  undefined,
);

interface WidgetReadySignalProps {
  children?: ReactNode;
  nextFrame?: boolean;
}

interface WidgetReadyProviderProps {
  children: ReactNode;
  onReady?: () => void;
}

export const WidgetReadyProvider = ({
  children,
  onReady,
}: WidgetReadyProviderProps) => {
  const readyCalledRef = useRef(false);
  const notifyReady = useCallback(() => {
    if (!readyCalledRef.current) {
      readyCalledRef.current = true;
      onReady?.();
    }
  }, [onReady]);

  if (!onReady) {
    return children;
  }

  return (
    <WidgetReadyContext.Provider value={notifyReady}>
      {children}
    </WidgetReadyContext.Provider>
  );
};

export const WidgetReadySignal = ({
  children,
  nextFrame = false,
}: WidgetReadySignalProps) => {
  const onReady = useContext(WidgetReadyContext);

  useEffect(() => {
    if (!onReady) return;

    if (!nextFrame) {
      onReady();
      return;
    }

    const animationFrameId = requestAnimationFrame(onReady);

    return () => cancelAnimationFrame(animationFrameId);
  }, [nextFrame, onReady]);

  return children;
};
